/**
 * The directory — "Your Angels" (front-door §6, front-door-impl §11.5).
 *
 * Lists ONLY the asking account's linked Agencies (the backend's /api/whoami
 * ends in WHERE account_id = ?). "Open" authorizes an SSO login: the backend
 * mints a vouch and returns an auto-POST page that delivers the token to the
 * Agency's exact registered callback (never a query string). We write that page
 * into the document so the browser performs the cross-site POST.
 *
 * The card is the front-door person-card from experience.md reused verbatim
 * (§6/§7): the Angel's portrait (Avatar.tsx, in the Angel's OWN accent — the
 * desk carries the Angel's identity, not house brass), a live presence dot, and
 * the one-line sticky note. So a user who's "seen one Angel" recognizes the
 * desk instantly.
 *
 * Two common-case rules from §6:
 *  - SINGLE Angel → skip the directory entirely and deep-link straight into the
 *    room (the aggregated view never gets in the way of the common case).
 *  - EMPTY → "No Angels yet."
 *
 * The page chrome stays house brass — the hub belongs to no Angel.
 */
import { useEffect, useRef, useState } from "react";
import { Avatar, Button, PresenceDot } from "@charlie/design/primitives";
import { whoami, fetchAngels, logout, type Account, type Angel } from "../lib/api";

export default function DirectoryPage() {
  const [account, setAccount] = useState<Account | null | "loading">("loading");
  const [angels, setAngels] = useState<Angel[] | null>(null); // null = not loaded yet
  const [opening, setOpening] = useState<string | null>(null);
  // Guards the single-Angel auto-open so it fires exactly once.
  const autoOpened = useRef(false);

  const open = async (agency: Angel) => {
    setOpening(agency.agency_id);
    const res = await fetch("/api/sso/authorize", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ agencyId: agency.agency_id }),
    });
    if (!res.ok) {
      setOpening(null);
      return;
    }
    const body = (await res.json()) as { html: string };
    // Replace the document with the auto-POST page; it submits the vouch to the
    // Agency's exact callback on load.
    document.open();
    document.write(body.html);
    document.close();
  };

  useEffect(() => {
    void (async () => {
      const me = await whoami();
      setAccount(me.account);
      if (me.account) setAngels(await fetchAngels());
    })();
  }, []);

  // §6: a single Angel skips the directory entirely — deep-link straight in.
  // We do it after the list loads (not on the server) so the rare 0/2+ cases
  // still render the desk. Runs once; `open` rewrites the document, so there is
  // no flash of the list for the single-Angel common case.
  useEffect(() => {
    if (!angels || autoOpened.current) return;
    if (angels.length === 1) {
      autoOpened.current = true;
      void open(angels[0]!);
    }
  }, [angels]);

  // Still resolving whoami → the quiet placeholder.
  if (account === "loading") {
    return <div className="mx-auto max-w-md px-6 py-12 text-center text-ink-soft">…</div>;
  }
  // Logged out → the login door. This MUST come BEFORE the angels-loading guard:
  // a logged-out visitor never loads angels (the effect only fetches them once an
  // account is known), so `angels` stays null and would otherwise wedge the page
  // on the "…" placeholder forever instead of bouncing to /login.
  if (account === null) {
    window.location.assign("/login");
    return null;
  }
  // Authenticated, but the Angel list hasn't arrived yet.
  if (angels === null) {
    return <div className="mx-auto max-w-md px-6 py-12 text-center text-ink-soft">…</div>;
  }
  // A single Angel is being deep-linked (the document is about to be rewritten);
  // hold a quiet "opening…" rather than flashing the list.
  if (angels.length === 1) {
    return (
      <div className="mx-auto max-w-md px-6 py-12 text-center text-ink-soft">
        <span className="italic">opening {angels[0]!.label}…</span>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col gap-6 px-6 py-12">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="overline-label">Welcome back</p>
          <h1 className="font-display text-[24px] text-ink">Your Angels</h1>
        </div>
        <button
          className="text-sm text-ink-soft underline-offset-2 hover:underline"
          onClick={async () => {
            await logout();
            window.location.assign("/login");
          }}
        >
          Sign out
        </button>
      </header>

      {angels.length === 0 ? (
        <div className="rounded-card border border-hairline bg-surface p-6 text-center">
          <p className="font-display text-lg text-ink">No Angels yet.</p>
          <p className="mt-1 text-sm text-ink-soft">
            When you're added to another Angel, it shows up here — one tap to open.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {angels.map((a) => (
            <AngelCard key={a.agency_id} angel={a} opening={opening === a.agency_id} onOpen={() => open(a)} />
          ))}
        </ul>
      )}

      <a href="/account" className="text-sm text-brass-deep underline-offset-2 hover:underline">
        Manage your account →
      </a>
    </div>
  );
}

/**
 * One directory row = the front-door person-card (verbatim, §7): the portrait in
 * the Angel's own accent, a live presence dot, and the one-line note.
 */
function AngelCard(props: { angel: Angel; opening: boolean; onOpen: () => void }) {
  const { angel } = props;
  const accent = accentFor(angel.agency_id);
  return (
    <li className="flex items-center justify-between gap-3 rounded-card border border-hairline bg-surface p-4">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar agentId={slug(angel.label)} name={angel.label} accent={accent} size={44} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-display text-ink">{angel.label}</p>
            {/* Live presence. v1 has no cross-Agency liveness fan-out (§5.7,
                deferred), so a linked desk reads "reachable"; the label is the
                honest text presence for screen readers. */}
            <PresenceDot kind="ok" srLabel={`${angel.label} is reachable`} />
          </div>
          <p className="truncate text-sm text-ink-soft">here and reachable</p>
        </div>
      </div>
      <Button variant="accent" disabled={props.opening} onClick={props.onOpen}>
        {props.opening ? <span className="italic">opening…</span> : "Open"}
      </Button>
    </li>
  );
}

/** A URL-safe, stable id for the Avatar portrait lookup (/avatars/<id>.png). */
function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "angel";
}

/**
 * A stable, distinct accent per Angel so each desk reads as its own room (the
 * front-door card is the Angel's, not house brass). Derived deterministically
 * from the agency id; Scott's house green leads the palette.
 */
const ACCENTS = ["#2f5d50", "#3a5a7d", "#7d4a3a", "#5a4a7d", "#3a7d5a", "#7d6a3a"];
function accentFor(agencyId: string): string {
  let h = 0;
  for (let i = 0; i < agencyId.length; i++) h = (Math.imul(31, h) + agencyId.charCodeAt(i)) | 0;
  return ACCENTS[Math.abs(h) % ACCENTS.length]!;
}
